package com.rainbowclubs.rainbow.presentation.select_club

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.rainbowclubs.rainbow.data.models.Club
import com.rainbowclubs.rainbow.data.models.Membership
import com.rainbowclubs.rainbow.data.responses.SelectClubResponse
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.blueBackground
import com.rainbowclubs.rainbow.presentation.theme.gradient2
import com.rainbowclubs.rainbow.presentation.theme.transparent
import com.rainbowclubs.rainbow.presentation.theme.yellow_background
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.general_error
import rainbowclubs.composeapp.generated.resources.ic_chevron_right
import rainbowclubs.composeapp.generated.resources.join_a_club
import rainbowclubs.composeapp.generated.resources.logo_image_cd
import rainbowclubs.composeapp.generated.resources.new_to_rainbow_clubs
import rainbowclubs.composeapp.generated.resources.rainbow_clubs
import rainbowclubs.composeapp.generated.resources.request_to_join_button
import rainbowclubs.composeapp.generated.resources.try_again_button
import rainbowclubs.composeapp.generated.resources.view_button
import rainbowclubs.composeapp.generated.resources.your_clubs

@Composable
fun SelectClubScreen(
    viewModel: SelectClubViewModel = koinViewModel(),
    navigateTo: (SelectClubViewModel.NavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.fetchData()
    }

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = Color.Transparent,
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(top = paddingValues.calculateTopPadding())
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,

            ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(180.dp),
                contentAlignment = Alignment.TopCenter,
            ) {
                Header()
            }

            when (screenState) {
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = { viewModel.fetchData() },
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Loading -> LoadingScreen()
                is ScreenState.Content -> {
                    val data = (screenState as ScreenState.Content<SelectClubResponse>).data
                    SelectClubContent(
                        memberships = data.memberships,
                        clubs = data.clubs,
                        onClick = { club, isRequest ->
                            viewModel.onButtonClicked(club, isRequest)
                        },
                    )
                }
            }

            Spacer(Modifier.height(32.dp))
        }
    }
}

@Composable
private fun SelectClubContent(
    memberships: List<Membership>,
    clubs: List<Club>,
    modifier: Modifier = Modifier,
    onClick: (club: Club, isRequest: Boolean) -> Unit,
) {

    Column(
        modifier = modifier
            .fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(24.dp),
    ) {
        if (memberships.isNotEmpty()) {
            ClubSection(
                title = stringResource(Res.string.your_clubs),
                backgroundColor = MaterialTheme.colorScheme.blueBackground,
                items = memberships.map {
                    Club(
                        id = it.clubId,
                        name = it.clubName,
                        description = null,
                        membershipData = null,
                    )
                },
                titleColor = MaterialTheme.colorScheme.onBackground,
                buttonText = stringResource(Res.string.view_button),
                isRequest = false,
                onClick = onClick,
            )
        }

        if (clubs.isNotEmpty()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable { }
                    .padding(vertical = 8.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = stringResource(Res.string.new_to_rainbow_clubs),
                    fontSize = 16.sp,
                    fontWeight = FontWeight.Medium,
                    color = MaterialTheme.colorScheme.onBackground,
                )
                Icon(
                    painter = painterResource(Res.drawable.ic_chevron_right),
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onBackground,
                    modifier = Modifier.size(20.dp),
                )
            }

            ClubSection(
                title = stringResource(Res.string.join_a_club),
                titleColor = MaterialTheme.colorScheme.onBackground,
                backgroundColor = MaterialTheme.colorScheme.yellow_background,
                items = clubs,
                buttonText = stringResource(Res.string.request_to_join_button),
                isRequest = true,
                onClick = onClick,
            )
        }
    }
}

@Composable
private fun ClubSection(
    title: String,
    backgroundColor: Color,
    items: List<Club>,
    buttonText: String,
    modifier: Modifier = Modifier,
    titleColor: Color,
    isRequest: Boolean,
    onClick: (club: Club, isRequest: Boolean) -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(24.dp))
            .background(backgroundColor)
            .padding(16.dp),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = title,
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                color = titleColor,
            )
            // TODO create a new screens with pagination
//            Icon(
//                painter = painterResource(Res.drawable.ic_chevron_right),
//                contentDescription = null,
//                tint = titleColor,
//                modifier = Modifier.size(20.dp)
//            )
        }

        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            items.forEach { item ->
                ClubItem(
                    item = item,
                    buttonText = buttonText,
                    onClick = onClick,
                    isRequest = isRequest,
                )
            }
        }
    }
}

@Composable
private fun ClubItem(
    item: Club,
    isRequest: Boolean,
    buttonText: String,
    modifier: Modifier = Modifier,
    onClick: (club: Club, isRequest: Boolean) -> Unit,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        color = MaterialTheme.colorScheme.surface,
    ) {
        Row(
            modifier = Modifier
                .padding(12.dp)
                .fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(CircleShape)
                    .background(
                        MaterialTheme.colorScheme.gradient2,
                    ),
                contentAlignment = Alignment.Center,
            ) {
                // TODO Add club logo
                Box(
                    modifier = Modifier.size(24.dp)
                        .background(Color.Gray.copy(alpha = 0.2f), CircleShape)
                )
            }

            Spacer(Modifier.width(12.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = item.name,
                    fontSize = 15.sp,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface,
                )
//                Text(
//                    text = stringResource(Res.string.members_count, item.memberCount),
//                    fontSize = 13.sp,
//                    color = Color(0xFF888888)
//                )
            }

            Button(
                onClick = {
                    onClick(item, isRequest)
                },
                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.transparent),
                shape = RoundedCornerShape(12.dp),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 0.dp),
                modifier = Modifier.height(32.dp).clip(
                    RoundedCornerShape(12.dp)
                ).background(
                    MaterialTheme.colorScheme.gradient2,
                )
            ) {
                Text(
                    text = buttonText,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface,
                )
            }
        }
    }
}

@Composable
private fun Header(modifier: Modifier = Modifier) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = modifier.fillMaxWidth().padding(top = 24.dp),
    ) {
        Image(
            painter = painterResource(Res.drawable.rainbow_clubs),
            contentDescription = stringResource(Res.string.logo_image_cd),
            modifier = Modifier.size(80.dp),
            contentScale = ContentScale.Fit,
        )

        Spacer(Modifier.height(8.dp))

        Text(
            text = "RAINBOW CLUBS",
            fontSize = 24.sp,
            fontWeight = FontWeight.ExtraBold,
            color = MaterialTheme.colorScheme.outline,
            letterSpacing = 0.5.sp,
        )
    }
}

@Composable
@Preview
private fun SelectClubScreenPreview() {
    RainbowClubTheme {
        SelectClubContent(
            memberships = listOf(
                Membership(
                    userDisplayName = "John Doe",
                    userId = "12345",
                    clubName = "Rainbow Club",
                    clubId = "12345",
                    membershipId = "12345",
                ),
                Membership(
                    userDisplayName = "John Doe",
                    userId = "12345",
                    clubName = "Rainbow Club 2",
                    clubId = "12345",
                    membershipId = "12345",
                ),
            ),
            clubs = listOf(
                Club(
                    id = "12345",
                    name = "Rainbow Club",
                    description = null,
                    membershipData = null,
                )
            ),
            onClick = { _, _ -> },
        )
    }
}

@Composable
@Preview
private fun HeaderPreview() {
    RainbowClubTheme {
        Header()
    }
}
